import { useState, useRef, useId, useCallback } from 'react';

// ============================================================================
// Hook Props & Return Types
// ============================================================================

export interface UseTextFieldProps {
  value?: string | undefined;
  defaultValue?: string | undefined;
  error?: boolean | undefined;
  errorMessage?: string | undefined;
  disabled?: boolean | undefined;
  placeholder?: string | undefined;
  id?: string | undefined;
  customLabelColor?: string | undefined;
  onChange?: ((e: React.ChangeEvent<HTMLInputElement>) => void) | undefined;
  onClear?: (() => void) | undefined;
  onFocus?: ((e: React.FocusEvent<HTMLInputElement>) => void) | undefined;
  onBlur?: ((e: React.FocusEvent<HTMLInputElement>) => void) | undefined;
}

export interface UseTextFieldReturn {
  inputId: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  hasValue: boolean;
  hasError: boolean;
  isFloating: boolean;
  isFocused: boolean;
  labelColor: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleClear: () => void;
  handleFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleContainerClick: () => void;
}

// ============================================================================
// useTextField Hook
// ============================================================================

export const useTextField = ({
  value: controlledValue,
  defaultValue = '',
  error = false,
  errorMessage,
  disabled = false,
  placeholder,
  id: providedId,
  customLabelColor,
  onChange,
  onClear,
  onFocus,
  onBlur,
}: UseTextFieldProps): UseTextFieldReturn => {
  const generatedId = useId();
  const inputId = providedId || generatedId;
  const inputRef = useRef<HTMLInputElement>(null);

  // Internal state
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);

  // Controlled vs uncontrolled
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const hasValue = value.length > 0;
  const hasError = error || !!errorMessage;
  const isFloating = isFocused || hasValue || !!placeholder;

  // Get label color based on state
  const getLabelColor = useCallback((): string => {
    if (disabled) {
      return '#b6bcc1'; // neutral-60
    }
    if (isFocused) {
      return '#2a4455'; // primary-color-40
    }
    return customLabelColor || '#6a7278'; // custom or neutral-40
  }, [disabled, isFocused, customLabelColor]);

  const labelColor = getLabelColor();

  // Handlers
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (controlledValue === undefined) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    },
    [controlledValue, onChange]
  );

  const handleClear = useCallback(() => {
    if (controlledValue === undefined) {
      setInternalValue('');
    }
    onClear?.();
    inputRef.current?.focus();
  }, [controlledValue, onClear]);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleContainerClick = useCallback(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  return {
    inputId,
    inputRef,
    value,
    hasValue,
    hasError,
    isFloating,
    isFocused,
    labelColor,
    handleChange,
    handleClear,
    handleFocus,
    handleBlur,
    handleContainerClick,
  };
};
